import type { SiteDetail } from "@plenum/shared/api";
import { lazy, Suspense } from "react";
import { ApiError, useClimate, useSettings } from "../../lib/api";
import { fmtInt } from "../../lib/format";
import { FRAME_LABEL } from "../../lib/tiers";
import { Pill } from "../Badge";

const CoolingCharts = lazy(() => import("./CoolingCharts"));

/** Cooling potential tab (PRD §8.7, §12.3): dx chart, monthly gain, parameter panel, water and indicative value. */
export function CoolingTab({ detail }: { detail: SiteDetail }) {
  const q = useClimate(detail.site.id);
  const settings = useSettings();
  if (q.isLoading) return <p style={{ color: "var(--muted)" }}>Loading climate…</p>;
  if (q.error)
    return (
      <p style={{ color: "var(--danger)" }}>
        {q.error instanceof ApiError ? q.error.message : "Could not load climate"}
      </p>
    );
  const d = q.data;
  const cooling = (settings.data?.effective.cooling ?? {}) as Record<string, unknown>;
  const sens = (cooling.sens_pct_per_c ?? {}) as Record<string, number>;
  const frames = [...new Set(detail.units.filter((u) => u.in_band).map((u) => u.frame_class))];

  if (!d?.profile) {
    return (
      <div className="flex flex-col gap-3">
        <p style={{ color: "var(--muted)" }}>
          No climate profile yet. The pipeline's climate step fetches five years of hourly ERA5-Land
          weather for this site and the estimate step runs the cooling model per in-band unit. Both
          are gated on the Phase 3 model approval and an Open-Meteo key (docs/questions.md Q25).
        </p>
        <ParamPanel cooling={cooling} sens={sens} frames={frames} />
      </div>
    );
  }
  const p = d.profile;
  return (
    <div className="flex flex-col gap-4">
      <section className="grid grid-cols-3 gap-2">
        <Kpi
          label="Cooling hours / yr"
          value={fmtInt(p.cooling_hours)}
          sub={`T > ${String(cooling.T_on_c ?? 15)} °C, dx ≥ ${String(cooling.dx_min_g_kg ?? 1)} g/kg`}
        />
        <Kpi
          label="Est. MWh / yr"
          value={d.totals.est_mwh_yr === null ? "—" : fmtInt(d.totals.est_mwh_yr)}
          sub={`${d.estimates.length} in-band unit${d.estimates.length === 1 ? "" : "s"}`}
        />
        <Kpi
          label="Water m³ / yr"
          value={d.totals.water_m3_yr === null ? "—" : fmtInt(d.totals.water_m3_yr)}
          sub={
            d.totals.drain_m3_yr === null ? undefined : `${fmtInt(d.totals.drain_m3_yr)} m³ drain`
          }
        />
        <Kpi
          label="Hours above 30 °C"
          value={fmtInt(p.hours_above_30c)}
          sub={`${fmtInt(p.hours_above_35c)} above 35 °C`}
        />
        <Kpi
          label="Summer depression"
          value={
            p.mean_summer_depression_c === null
              ? "—"
              : `${p.mean_summer_depression_c.toFixed(1)} °C`
          }
          sub="Jun to Sep, 10:00 to 20:00"
        />
        <Kpi
          label="Design point (90 %)"
          value={p.dx_design_90 === null ? "—" : `${p.dx_design_90.toFixed(1)} g/kg`}
          sub={p.koppen ? `Köppen ${p.koppen}` : undefined}
        />
      </section>
      {d.series ? (
        <Suspense fallback={<p style={{ color: "var(--muted)" }}>Loading charts…</p>}>
          <CoolingCharts series={d.series} />
        </Suspense>
      ) : null}
      <section>
        <h3 className="eyebrow mb-1">Per unit</h3>
        <table className="tbl">
          <thead>
            <tr>
              <th>Gen</th>
              <th className="r">MW</th>
              <th className="r">Cooling h</th>
              <th className="r">MWh/yr</th>
              <th className="r">Avg MW</th>
              <th className="r">Peak MW</th>
              <th className="r">Water m³</th>
              <th className="r">Value USD</th>
            </tr>
          </thead>
          <tbody>
            {d.estimates.map((e) => (
              <tr key={e.unit_id}>
                <td className="num">{e.eia_generator_id ?? e.unit_id}</td>
                <td className="num r">{e.nameplate_mw ?? "—"}</td>
                <td className="num r">{fmtInt(e.estimate.cooling_hours)}</td>
                <td className="num r">{fmtInt(e.estimate.mwh_gain_yr)}</td>
                <td className="num r">{e.estimate.avg_mw_gain?.toFixed(2) ?? "—"}</td>
                <td className="num r">{e.estimate.peak_mw_gain?.toFixed(2) ?? "—"}</td>
                <td className="num r">{fmtInt(e.estimate.water_m3_yr)}</td>
                <td className="num r">{fmtInt(e.estimate.indicative_value_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-1 text-[11px]" style={{ color: "var(--muted-2)" }}>
          Indicative value uses a summer on-peak price by balancing authority (Settings). It is an
          order-of-magnitude figure for prioritisation, not a revenue forecast. Model{" "}
          {d.model.cooling_version}; source {p.source}, years {p.years.join(", ")}.
        </p>
      </section>
      <ParamPanel cooling={cooling} sens={sens} frames={frames} />
    </div>
  );
}

function ParamPanel({
  cooling,
  sens,
  frames,
}: {
  cooling: Record<string, unknown>;
  sens: Record<string, number>;
  frames: string[];
}) {
  return (
    <section>
      <h3 className="eyebrow mb-1">Model parameters</h3>
      <dl className="kv text-[12px]">
        <dt>T_on / dx_min</dt>
        <dd className="num">
          {String(cooling.T_on_c ?? "—")} °C / {String(cooling.dx_min_g_kg ?? "—")} g/kg
        </dd>
        <dt>Effectiveness ε / dT_max</dt>
        <dd className="num">
          {String(cooling.epsilon ?? "—")} / {String(cooling.dT_max_c ?? "—")} °C
        </dd>
        <dt>Sensitivity</dt>
        <dd>
          {frames.map((f) => (
            <Pill key={f}>
              {FRAME_LABEL[f] ?? f} {sens[f] ?? "—"} % per °C
            </Pill>
          ))}
        </dd>
        <dt>Drain</dt>
        <dd className="num">{String(cooling.drain_fraction ?? "—")} of water flow</dd>
      </dl>
      <p className="mt-1 text-[11px]" style={{ color: "var(--muted-2)" }}>
        Site-level overrides and re-run ("POST /api/sites/:id/estimate") arrive with the scoring
        proposal, which puts the TypeScript model in packages/shared. Org-wide values are edited in
        Settings.
      </p>
    </section>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div
      className="rounded border px-2 py-1.5"
      style={{ borderColor: "var(--line)", background: "var(--panel-2)" }}
    >
      <div className="eyebrow">{label}</div>
      <div className="num text-[15px]">{value}</div>
      {sub ? (
        <div className="text-[11px]" style={{ color: "var(--muted-2)" }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}
